import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  PawPrint,
  BarChart3,
  Stethoscope,
  ClipboardList,
  Apple,
  Home
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

// Import design system components and tokens
import { 
  typography, 
  spacing,
  tabSystem
} from '@/components/business/design-system/businessDesignTokens';

// Import tab components
import { DashboardTab } from './components/DashboardTab';
import { AnimalsTab } from './components/AnimalsTab';
import { HealthTab } from './components/HealthTab';
import { TasksTab } from './components/TasksTab';
import { FeedingTab } from './components/FeedingTab';
import { EnclosuresTab } from './components/EnclosuresTab';

// Data Types
export interface Animal {
  id: string;
  name: string;
  species: string;
  sex: 'Male' | 'Female';
  age: string;
  dateOfBirth: string;
  microchipId?: string;
  intake: {
    date: string;
    origin: string;
    reason: string;
  };
  currentLocation: string;
  status: 'Healthy' | 'Under Treatment' | 'Quarantine' | 'Critical';
  physicalCharacteristics: {
    weight?: number;
    height?: number;
    markings?: string;
    color?: string;
  };
  photos: string[];
  notes?: string;
}

export interface HealthRecord {
  id: string;
  animalId: string;
  date: string;
  type: 'Consultation' | 'Treatment' | 'Vaccination' | 'Checkup' | 'Lab Result';
  veterinarian: string;
  symptoms?: string;
  diagnosis?: string;
  treatment?: string;
  medication?: {
    name: string;
    dosage: string;
    frequency: string;
    duration: string;
  }[];
  nextDue?: string;
  notes?: string;
  weight?: number;
  bodyConditionScore?: number;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  animalId?: string;
  enclosureId?: string;
  assignedTo: string;
  dueDate: string;
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  status: 'To Do' | 'In Progress' | 'Completed';
  category: 'Health' | 'Feeding' | 'Cleaning' | 'Maintenance' | 'Observation' | 'Other';
  recurring?: {
    frequency: 'Daily' | 'Weekly' | 'Monthly';
    endDate?: string;
  };
}

export interface FeedingRecord {
  id: string;
  animalId: string;
  date: string;
  time: string;
  foodType: string;
  quantity: number;
  unit: string;
  consumption: 'Full' | 'Partial' | 'Refused';
  notes?: string;
  fedBy: string;
}

export interface Enclosure {
  id: string;
  name: string;
  type: 'Indoor' | 'Outdoor' | 'Mixed';
  size: number;
  capacity: number;
  currentAnimals: string[];
  lastCleaned?: string;
  maintenanceSchedule: {
    frequency: string;
    lastDone: string;
    nextDue: string;
  };
  environmentalNotes?: string;
}

// Mock Data
const mockAnimals: Animal[] = [
  {
    id: 'a1',
    name: 'Luna',
    species: 'African Elephant',
    sex: 'Female',
    age: '8 years',
    dateOfBirth: '2016-03-15',
    microchipId: 'MC123456789',
    intake: {
      date: '2018-06-20',
      origin: 'Kruger National Park',
      reason: 'Orphaned calf'
    },
    currentLocation: 'Elephant Sanctuary A',
    status: 'Healthy',
    physicalCharacteristics: {
      weight: 2800,
      height: 220,
      markings: 'Distinctive ear notch on left ear',
      color: 'Dark grey'
    },
    photos: [],
    notes: 'Very social, responds well to enrichment activities'
  },
  {
    id: 'a2',
    name: 'Simba',
    species: 'African Lion',
    sex: 'Male',
    age: '5 years',
    dateOfBirth: '2019-07-10',
    microchipId: 'MC987654321',
    intake: {
      date: '2020-02-14',
      origin: 'Confiscated from illegal trade',
      reason: 'Rescue from captivity'
    },
    currentLocation: 'Large Carnivore Enclosure 1',
    status: 'Under Treatment',
    physicalCharacteristics: {
      weight: 180,
      markings: 'Scar on left shoulder',
      color: 'Golden brown with dark mane'
    },
    photos: [],
    notes: 'Recovering from minor leg injury, good appetite'
  },
  {
    id: 'a3',
    name: 'Grace',
    species: 'Giraffe',
    sex: 'Female',
    age: '3 years',
    dateOfBirth: '2021-05-03',
    microchipId: 'MC456789123',
    intake: {
      date: '2021-05-04',
      origin: 'Born in sanctuary',
      reason: 'Sanctuary birth'
    },
    currentLocation: 'Giraffe Habitat',
    status: 'Healthy',
    physicalCharacteristics: {
      weight: 950,
      height: 450,
      markings: 'Distinctive patch pattern on neck',
      color: 'Light brown with dark patches'
    },
    photos: [],
    notes: 'Born in sanctuary, excellent development'
  }
];

const mockHealthRecords: HealthRecord[] = [
  {
    id: 'h1',
    animalId: 'a2',
    date: '2024-06-15',
    type: 'Treatment',
    veterinarian: 'Dr. Sarah Johnson',
    symptoms: 'Limping on left hind leg',
    diagnosis: 'Minor muscle strain',
    treatment: 'Rest and anti-inflammatory medication',
    medication: [{
      name: 'Meloxicam',
      dosage: '15mg',
      frequency: 'Once daily',
      duration: '7 days'
    }],
    nextDue: '2024-06-22',
    weight: 180,
    notes: 'Monitor for improvement over next week'
  }
];

const mockTasks: Task[] = [
  { 
    id: 't1', 
    title: 'Morning Health Check - Luna',
    description: 'Perform daily health observation and record any changes',
    animalId: 'a1',
    assignedTo: 'Dr. Sarah Johnson',
    dueDate: '2024-06-20',
    priority: 'High',
    status: 'To Do',
    category: 'Health',
    recurring: {
      frequency: 'Daily'
    }
  },
  { 
    id: 't2', 
    title: 'Enclosure Cleaning - Lion Area',
    description: 'Deep clean and sanitize the large carnivore enclosure',
    enclosureId: 'e1',
    assignedTo: 'Mike Thompson',
    dueDate: '2024-06-20',
    priority: 'Medium',
    status: 'In Progress',
    category: 'Cleaning'
  }
];

const mockFeedingRecords: FeedingRecord[] = [
  {
    id: 'f1',
    animalId: 'a1',
    date: '2024-06-20',
    time: '07:00',
    foodType: 'Hay and fresh browse',
    quantity: 150,
    unit: 'kg',
    consumption: 'Full',
    fedBy: 'John Smith'
  }
];

const mockEnclosures: Enclosure[] = [
  {
    id: 'e1',
    name: 'Large Carnivore Enclosure 1',
    type: 'Mixed',
    size: 5000,
    capacity: 4,
    currentAnimals: ['a2'],
    lastCleaned: '2024-06-19',
    maintenanceSchedule: {
      frequency: 'Daily cleaning, weekly deep clean',
      lastDone: '2024-06-19',
      nextDue: '2024-06-20'
    },
    environmentalNotes: 'Shade structures installed, water feature operational'
  },
  {
    id: 'e2',
    name: 'Elephant Sanctuary A',
    type: 'Outdoor',
    size: 15000,
    capacity: 6,
    currentAnimals: ['a1'],
    lastCleaned: '2024-06-19',
    maintenanceSchedule: {
      frequency: 'Daily maintenance, monthly deep maintenance',
      lastDone: '2024-06-19',
      nextDue: '2024-06-20'
    },
    environmentalNotes: 'Mud wallow maintained, enrichment items rotated weekly'
  }
];

export function LivestockManagementPage() {
  const { t } = useTranslation('business', { keyPrefix: 'livestock' });
  const { t: tc } = useTranslation('common');
  
  // State Management
  const [animals, setAnimals] = useState<Animal[]>(mockAnimals);
  const [healthRecords, setHealthRecords] = useState<HealthRecord[]>(mockHealthRecords);
  const [tasks, setTasks] = useState<Task[]>(mockTasks);
  const [feedingRecords, setFeedingRecords] = useState<FeedingRecord[]>(mockFeedingRecords);
  const [enclosures, setEnclosures] = useState<Enclosure[]>(mockEnclosures);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Quick stats for dashboard
  const stats = {
    totalAnimals: animals.length,
    healthyAnimals: animals.filter(a => a.status === 'Healthy').length,
    pendingTasks: tasks.filter(t => t.status === 'To Do').length,
    upcomingFeedings: feedingRecords.length, // This would be today's feedings in real app
    alerts: healthRecords.filter(h => h.nextDue && new Date(h.nextDue) <= new Date()).length
  };

  return (
    <div className={spacing.sectionGap}>
      {/* Streamlined Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-2"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-md">
            <PawPrint className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className={cn(typography.h1, "bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 bg-clip-text text-transparent")}>
              {t('wildlifeSanctuaryManagement')}
            </h1>
            <p className="text-sm text-slate-600 mt-0.5">
              {t('comprehensiveAnimalCare')}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Unified Tab System with Business Design */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className={tabSystem.container.base}>
        {/* Navigation Tabs */}
        <div className={tabSystem.container.listWrapper}>
          <TabsList className={tabSystem.container.list}>
            <TabsTrigger 
              value="dashboard" 
              className={tabSystem.trigger.complete}
            >
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">{t('dashboard')}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="animals"
              className={tabSystem.trigger.complete}
            >
              <PawPrint className="w-4 h-4" />
              <span className="hidden sm:inline">{t('animals')}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="health"
              className={tabSystem.trigger.complete}
            >
              <Stethoscope className="w-4 h-4" />
              <span className="hidden sm:inline">{t('health')}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="tasks"
              className={tabSystem.trigger.complete}
            >
              <ClipboardList className="w-4 h-4" />
              <span className="hidden sm:inline">{t('tasks')}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="feeding"
              className={tabSystem.trigger.complete}
            >
              <Apple className="w-4 h-4" />
              <span className="hidden sm:inline">{t('feeding')}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="enclosures"
              className={tabSystem.trigger.complete}
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">{t('enclosures')}</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Tab Content */}
        <TabsContent value="dashboard" className={tabSystem.content.base}>
          <DashboardTab 
            stats={stats}
            animals={animals}
            tasks={tasks}
            healthRecords={healthRecords}
            onNavigateToTab={setActiveTab}
          />
        </TabsContent>

        <TabsContent value="animals" className={tabSystem.content.base}>
          <AnimalsTab 
            animals={animals}
            setAnimals={setAnimals}
            enclosures={enclosures}
          />
        </TabsContent>

        <TabsContent value="health" className={tabSystem.content.base}>
          <HealthTab 
            animals={animals}
            healthRecords={healthRecords}
            setHealthRecords={setHealthRecords}
          />
        </TabsContent>

        <TabsContent value="tasks" className={tabSystem.content.base}>
          <TasksTab 
            tasks={tasks}
            setTasks={setTasks}
            animals={animals}
            enclosures={enclosures}
          />
        </TabsContent>

        <TabsContent value="feeding" className={tabSystem.content.base}>
          <FeedingTab 
            animals={animals}
            feedingRecords={feedingRecords}
            setFeedingRecords={setFeedingRecords}
          />
        </TabsContent>

        <TabsContent value="enclosures" className={tabSystem.content.base}>
          <EnclosuresTab 
            enclosures={enclosures}
            setEnclosures={setEnclosures}
            animals={animals}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
} 